import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { IntegrationDiagram } from "../components/IntegrationDiagram.js";
import { getGame } from "../content/games.js";
import { PRICING_LINES, PRICING_SUMMARY } from "../content/pricing.js";
import { LICENSING_STANCE } from "../content/jurisdictions.js";
import { NotFound } from "./NotFound.js";

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

const RULES: Array<{ step: number; title: string; body: string }> = [
  {
    step: 1,
    title: "Stake crypto",
    body: "Player places a bet from their operator-held balance. No deposits to Vault Breaker Studios.",
  },
  {
    step: 2,
    title: "Watch the multiplier",
    body: "The multiplier climbs from 1.00× in real time. Five vault locks sit across the screen.",
  },
  {
    step: 3,
    title: "Cash out at any moment",
    body: "Click CASH OUT and the stake is credited back with the current multiplier as a payout.",
  },
  {
    step: 4,
    title: "Or watch the vault seal",
    body: "Locks crack at random intervals. If all five break before cashout, the vault seals and the stake is lost.",
  },
];

/** Per-game math sheet. Was previously a single constant copy-pasted across
 *  every game (showing crash-specific "Bust CDF" rows on Mines and Mastermind
 *  detail pages — wrong). Now derived per slug from the game's own math. */
function mathRowsFor(slug: string, rtpPercent: number): Array<[string, string]> {
  const houseEdge = (100 - rtpPercent).toFixed(1) + "%";
  const rtp = rtpPercent.toFixed(1) + "%";
  const common: Array<[string, string]> = [
    ["House edge", houseEdge],
    ["RTP", rtp],
    ["Hash", "SHA-256, commit-reveal"],
    ["Entropy", "server seed (32B) + client seed + nonce"],
  ];
  if (slug === "vault-breaker") {
    return [
      common[0]!,
      common[1]!,
      ["Advertised round length", "30 seconds"],
      ["Bust CDF", "P(crash ≥ c) ≈ 99 / (100·c)"],
      ["Instabust rate", "~2.0%"],
      ["Max multiplier", "1000×"],
      common[2]!,
      common[3]!,
    ];
  }
  if (slug === "pressure-plate") {
    return [
      common[0]!,
      common[1]!,
      ["Board", "5×5 = 25 tiles"],
      ["Mine range", "1–24 mines (player-selected)"],
      ["Payout formula", "∏ (25−i)/(25−i−mines) × 0.95"],
      ["Max multiplier (24 mines)", "25.00×"],
      common[2]!,
      common[3]!,
    ];
  }
  if (slug === "cipher-crack") {
    return [
      common[0]!,
      common[1]!,
      ["Round length", "30 seconds"],
      ["Code", "4 digits, each uniform on [0,9]"],
      ["Attempts", "4 max"],
      ["Crack payouts", "1st 20× · 2nd 5× · 3rd 2.5× · 4th 1.3×"],
      ["Cashout multipliers", "0.35× / 0.65× / 0.95× (after 1/2/3 wrong guesses)"],
      common[2]!,
      common[3]!,
    ];
  }
  return common;
}

/** Map a game slug to the corresponding interactive demo route. Vault Breaker
 *  (the original crash game) lives at /play; later titles each get their own
 *  dedicated subpath. Falls back to /play so unknown slugs at least take the
 *  player somewhere usable. */
function playUrlFor(slug: string): string {
  if (slug === "pressure-plate") return "/play/pressure-plate";
  if (slug === "cipher-crack") return "/play/cipher-crack";
  return "/play";
}

export function GameDetail() {
  const { slug } = useParams();
  const game = getGame(slug ?? "");
  const isMobile = useMediaQuery("(max-width: 720px)");
  if (!game || game.status !== "live") return <NotFound />;
  const playUrl = playUrlFor(game.slug);

  return (
    <>
      <Seo
        title={`${game.name} · Vault Breaker Studios`}
        description={game.tagline}
        path={`/games/${game.slug}`}
        ogImage={`/og-game-${game.slug}.png`}
      />

      {/* HERO */}
      <Section style={{ paddingBottom: 32 }}>
        <div
          style={{
            display: "grid",
            gap: 28,
            gridTemplateColumns: isMobile ? "1fr" : "minmax(300px, 1fr) minmax(320px, 1.2fr)",
            alignItems: "center",
          }}
        >
          <div>
            <Badge kind="live" style={{ marginBottom: 14 }}>
              FLAGSHIP · DEMO
            </Badge>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(48px, 7vw, 88px)",
                lineHeight: 0.95,
                margin: 0,
                color: "var(--fg-1)",
                textTransform: "uppercase",
              }}
            >
              {game.name}
            </h1>
            <p style={{ fontSize: 18, color: "var(--fg-2)", marginTop: 14, lineHeight: 1.55 }}>
              {game.description}
            </p>
            <p style={{ fontSize: 14, color: "var(--fg-3)", marginTop: 10 }}>
              Currently running against a play-money wallet on our side. Production rounds run on
              your wallet when we integrate.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
              <Link to={playUrl} style={{ textDecoration: "none" }}>
                <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
                  TRY DEMO
                </Button>
              </Link>
              <Link to="/contact" style={{ textDecoration: "none" }}>
                <Button style={{ padding: "14px 22px", fontSize: 15 }}>Contact sales</Button>
              </Link>
            </div>
          </div>
          <Card
            style={{
              padding: 0,
              overflow: "hidden",
              background: "var(--bg-2)",
              minHeight: isMobile ? 0 : 360,
              position: "relative",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--line)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span className="label">{isMobile ? "Demo" : "Embedded demo"}</span>
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>
                {playUrl} · play-money
              </span>
            </div>
            {isMobile ? (
              <div style={{ padding: 24, display: "grid", gap: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                  The full game runs best in its own window on mobile. Tap below to launch the
                  play-money demo in a new tab.
                </p>
                <a
                  href={playUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ textDecoration: "none" }}
                >
                  <Button variant="gold" fullWidth style={{ padding: "14px 20px", fontSize: 14 }}>
                    Try the demo →
                  </Button>
                </a>
              </div>
            ) : (
              <iframe
                src={playUrl}
                title={`${game.name} demo`}
                style={{
                  width: "100%",
                  height: 480,
                  border: 0,
                  display: "block",
                  background: "var(--bg-0)",
                }}
              />
            )}
          </Card>
        </div>
      </Section>

      {/* RULES */}
      <Section>
        <SectionHeading eyebrow="How it plays" title="Four steps. Thirty seconds." />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {RULES.map((r) => (
            <Card key={r.step} style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 36,
                  lineHeight: 1,
                  color: "var(--gold-bright)",
                }}
              >
                {String(r.step).padStart(2, "0")}
              </div>
              <h3 style={{ margin: 0, fontSize: 16 }}>{r.title}</h3>
              <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                {r.body}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* MATH SHEET */}
      <Section>
        <SectionHeading eyebrow="Math sheet" title="Fixed, published, verifiable" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {mathRowsFor(game.slug, game.rtpPercent).map(([k, v], i) => (
                <tr key={k} style={{ borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
                  <td
                    style={{
                      padding: "14px 20px",
                      fontFamily: "var(--font-ui)",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--fg-3)",
                      width: "45%",
                    }}
                  >
                    {k}
                  </td>
                  <td
                    style={{
                      padding: "14px 20px",
                      fontFamily: "var(--font-mono)",
                      fontSize: 13,
                      color: "var(--fg-1)",
                    }}
                  >
                    {v}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <p style={{ marginTop: 12, fontSize: 12, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
          · Engine source is shared with operators. Every round is reproducible from its server seed.
        </p>
      </Section>

      {/* INTEGRATION DIAGRAM */}
      <Section>
        <SectionHeading eyebrow="Integration" title="Three actors. One clock." />
        <IntegrationDiagram />
      </Section>

      {/* PRICING */}
      <Section>
        <SectionHeading
          eyebrow="Pricing"
          title="Free to integrate. 10% of the revenue."
          lead={PRICING_SUMMARY}
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {PRICING_LINES.map((p, i) => (
                <tr
                  key={p.label}
                  style={{
                    borderTop: i === 0 ? "none" : "1px solid var(--line)",
                    background: p.emphasis ? "rgba(212,162,76,0.06)" : "transparent",
                  }}
                >
                  <td
                    style={{
                      padding: "16px 22px",
                      fontFamily: "var(--font-ui)",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--fg-3)",
                      width: "55%",
                    }}
                  >
                    {p.label}
                  </td>
                  <td
                    style={{
                      padding: "16px 22px",
                      fontFamily: "var(--font-mono)",
                      fontSize: p.emphasis ? 16 : 14,
                      color: p.emphasis ? "var(--gold-bright)" : "var(--fg-1)",
                      fontWeight: p.emphasis ? 600 : 400,
                      textAlign: "right",
                    }}
                  >
                    {p.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </Section>

      {/* LICENSING */}
      <Section>
        <SectionHeading eyebrow="Licensing" title={LICENSING_STANCE.heading} />
        <Card style={{ display: "grid", gap: 14, padding: 28 }}>
          {LICENSING_STANCE.paragraphs.map((p, i) => (
            <p key={i} style={{ margin: 0, fontSize: 15, color: "var(--fg-2)", lineHeight: 1.6 }}>
              {p}
            </p>
          ))}
        </Card>
      </Section>

      {/* FOOTER CTA */}
      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to={playUrl} style={{ textDecoration: "none" }}>
            <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
              TRY DEMO
            </Button>
          </Link>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button style={{ padding: "14px 22px", fontSize: 15 }}>CONTACT SALES</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}
